import { Lock, PowerOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import { Card, EmptyState } from '../components/ui/index.js';
import { useAnyPermission } from '../hooks/usePermission.js';
import { hasSession, useAuthStore } from '../lib/authStore.js';
import { useCurrenciesStore } from '../lib/currenciesStore.js';
import { moduleEnabled, type ModuleKey } from '../lib/modules.js';
import { useEnabledModules } from '../lib/modulesStore.js';

export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const token = useAuthStore((s) => s.accessToken);
  // A token without the person and shop behind it is not a session (hasSession):
  // sign it out and ask for a proper login rather than run on guesses.
  const broken = useAuthStore((s) => !!s.accessToken && !hasSession(s));
  const location = useLocation();
  useEffect(() => { if (broken) useAuthStore.getState().clear(); }, [broken]);
  // Boot the currency-decimals registry for EVERY authed surface — including
  // the standalone window.open'd print/POS-display routes that bypass AppLayout
  // — so formatMoney honors backend per-currency decimals (IQD→0) there too.
  // Idempotent (load() returns early once loaded); never fires on public routes.
  useEffect(() => { if (token) void useCurrenciesStore.getState().load(); }, [token]);
  if (!token || broken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

/**
 * Say WHY, instead of bouncing to the dashboard.
 *
 * A silent redirect is undiagnosable by the person hitting it: the page they
 * asked for simply is not there, with no way to tell "you lack a permission"
 * from "this module is off" from "the link is broken". That cost real time here
 * — the cashback pages looked missing for weeks while the permission, the role
 * and the module flag were all correct on the server, and nothing on screen ever
 * named what was being checked.
 *
 * So the required permission is printed. It is not a secret — the person reading
 * it is signed in and just tried to open the page — and it is the one word that
 * turns "it doesn't work" into a message an admin can act on.
 */
export function RequirePermission({
  any,
  children,
}: {
  any: readonly string[];
  children: ReactNode;
}): JSX.Element {
  const ok = useAnyPermission(any);
  const { t } = useTranslation();
  if (!ok) {
    return (
      <Card>
        <EmptyState
          icon={<Lock size={28} />}
          title={t('noAccess.title', { defaultValue: 'You do not have access to this page' })}
          description={t('noAccess.hint', {
            defaultValue: 'It needs the {{perm}} permission. Ask an administrator to add it to your role — if it was granted recently, sign out and back in.',
            perm: any.join(' or '),
          })}
        />
      </Card>
    );
  }
  return <>{children}</>;
}

/** Gate a route on a tenant-toggleable module (says so, rather than redirecting). */
export function RequireModule({
  module,
  children,
}: {
  module: ModuleKey;
  children: ReactNode;
}): JSX.Element {
  const tenantId = useAuthStore((s) => s.tenant?.id);
  const enabled = useEnabledModules(tenantId);
  const { t } = useTranslation();
  // The name the switch carries in Settings → Modules, not the raw key: the
  // person reading this has to find that switch, and "manual_sales" is not what
  // it is labelled there. Falls back to the key if a module has no label yet.
  const { t: ts } = useTranslation('settings');
  if (!moduleEnabled(enabled, module)) {
    return (
      <Card>
        <EmptyState
          icon={<PowerOff size={28} />}
          title={t('moduleOff.title', { defaultValue: 'This feature is switched off' })}
          description={t('moduleOff.hint', {
            defaultValue: 'Turn on the {{module}} module in Settings → Modules to use this page.',
            module: ts(`modules.items.${module}.label`, { defaultValue: module }),
          })}
        />
      </Card>
    );
  }
  return <>{children}</>;
}
